import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit'
import {RootState} from 'store'
import {
  InvoiceGetResponse,
  UsageDetailResponse,
  getProviderLatest,
} from 'api/invoiceApis'
import {ProductTemplateType, ProductType} from 'features/invoice/invoiceSlice'
import {
  createInvoiceApi,
  CreateInvoicePayload,
  fetchProductDetailApi,
  getInvoiceByOrgAndMonth,
  GetInvoiceByOrgAndMonthRequest,
  getLicenseByOrgAndMonth,
  getLicenseDetailByOrg,
  getLicenseDetailByOrgLogs,
  InvoiceInformation,
  LicenseAnalyzeStatus,
  updateInvoiceApi,
  UpdateInvoicePayload,
} from 'api/productApi'
import {getListOrgDetailApi} from 'api/salesApi'
import i18n from 'i18n'
import {FILTERED_PACKAGE_ID} from 'pages/ProductOrderPages/constants'

export const getInvoiceData = createAsyncThunk(
  '/shop/licenses/v1/admin/invoice/by-org-and-month',
  async ({orgId, billingMonth}: GetInvoiceByOrgAndMonthRequest) => {
    let invoiceInformation: InvoiceInformation | null = null
    try {
      invoiceInformation = await getInvoiceByOrgAndMonth({
        orgId,
        billingMonth,
      }).then((res) => res.data)
    } catch (error) {
      console.error('Error fetching invoice data:', error)
    }

    const licenseResponse = await getLicenseByOrgAndMonth({
      orgId,
      billingMonth,
    })

    const licenseData = licenseResponse?.data?.[0]?.licenses || []
    const orgIdParams = new URLSearchParams({orgIds: orgId})
    const listLicenseByOrg = await getLicenseDetailByOrg(orgIdParams)
    const listGoods = await Promise.all(
      licenseData.map(async (i) => {
        return fetchProductDetailApi({
          goodsId: i.goodsId,
        }).then((res) => ({
          goodsId: i.goodsId,
          goodsName:
            res.data.goods.displayInfos.find(
              (info) =>
                info.langCode === (i18n.language === 'ko' ? 'ko' : 'en'),
            )?.name ?? '',
          goodsOffer: res.data.offer.offerItems,
          goodsTarget: res.data.sale.saleTarget.targets.find(
            (target) => target.targetType === 'Region',
          )?.targetId,
          licenseItems:
            listLicenseByOrg.data.find((item) => item.id === i.licenseId)
              ?.items || [],
        }))
      }),
    )
    const usageResponses = await Promise.all(
      licenseData.map(async (i) => {
        const [year, month] = billingMonth.split('-').map(Number)
        const currentMonth = new Date().getMonth() + 1
        const from = `${billingMonth}-01`
        const lastDay =
          month === currentMonth
            ? new Date().getDate()
            : new Date(year, month, 0).getDate()
        const to = `${billingMonth}-${String(lastDay).padStart(2, '0')}`
        const currentGoods = listGoods.find(
          (good) => good.goodsId === i.goodsId,
        )
        return getLicenseDetailByOrgLogs(i.licenseId, from, to).then((res) => ({
          goodsId: i.goodsId,
          goodsName: currentGoods?.goodsName || '',
          period: `${from} ~ ${to}`,
          usageLogs: res.data.usageLogs
            .map((usage) => {
              const currentLicenseItem = currentGoods?.licenseItems.find(
                (item) => item.id === usage.licenseItemId,
              )
              const packageItem = currentGoods?.goodsOffer.find(
                (offer) =>
                  offer.pkgItemId === currentLicenseItem?.packageItemId,
              )
              const packagePrice = packageItem?.unitPrices.find((price) =>
                currentGoods?.goodsTarget === 'Domestic'
                  ? price.currency === 'KRW'
                  : price.currency === 'USD',
              )
              return {
                usageId: usage.usageId,
                usageDateTime: usage.usageDateTime,
                analyzeStatus: usage.analyzeStatus,
                usageQuantity: usage.usageQuantity,
                packageItemId: currentLicenseItem?.packageItemId || '0',
                packagePrice: packagePrice?.price ?? 0,
              }
            })
            .filter(
              (usage) => !FILTERED_PACKAGE_ID.includes(usage.packageItemId),
            ),
        }))
      }),
    )

    if (invoiceInformation == null) {
      const responseOrgsInfo = await getListOrgDetailApi({
        orgId: [Number(orgId)],
      })
      const orgInfo = responseOrgsInfo.list.find(
        (item) => item.org?.id === Number(orgId),
      )
      const providerData = await getProviderLatest().then((res) => res.data)
      invoiceInformation = {
        consumer: {
          orgId: orgInfo?.org?.id || -1,
          name: orgInfo?.org?.name || '',
          email: orgInfo?.chiefUser?.email || '',
        },
        supplier: {
          ...providerData,
          businessNumber: providerData.companyNumber,
          ceoFirstName: providerData.ceoFirstname,
          ceoLastName: providerData.ceoLastname,
          contactNumber: providerData.phone,
          contactName: providerData.manager,
        },
        billing: {billingMonth},
      }
    }

    return {
      invoiceInformation,
      usageDetail: null,
      product: null,
      templates: null,
      usageSummaryData: [],
      usageSummaryTable: usageResponses.filter(
        (item) => item.usageLogs.length > 0,
      ),
    } as InvoiceViewState
  },
)

export const createInvoice = createAsyncThunk(
  '/shop/licenses/v1/admin/invoice',
  async (payload: CreateInvoicePayload, {rejectWithValue}) => {
    try {
      const response = await createInvoiceApi(payload)
      return response.data
    } catch (err) {
      return rejectWithValue(err.message)
    }
  },
)

export const updateInvoice = createAsyncThunk(
  '/shop/licenses/v1/admin/invoice/invoiceId',
  async (payload: UpdateInvoicePayload, {rejectWithValue}) => {
    try {
      const response = await updateInvoiceApi(payload)
      return response.data
    } catch (err) {
      return rejectWithValue(err.message)
    }
  },
)

export interface DataTableUsageSummary {
  productType: ProductType
  minCount: number
  maxCount: string | number
  count: number
  price: number
}

export interface UsageSummaryTableType {
  goodsId: string
  goodsName: string
  period: string
  usageLogs: {
    usageId: string
    usageQuantity: number
    usageDateTime: number
    packageItemId: string
    packagePrice: number
    analyzeStatus: LicenseAnalyzeStatus
  }[]
}

interface InvoiceViewState {
  loading?: boolean
  error?: any
  invoiceInformation: InvoiceInformation | null
  usageDetail: UsageDetailResponse[] | null
  product: InvoiceGetResponse | null
  templates: ProductTemplateType[] | null
  usageSummaryData: DataTableUsageSummary[] | null
  usageSummaryTable: UsageSummaryTableType[]
}

export const initialState: InvoiceViewState = {
  loading: false,
  error: null,
  invoiceInformation: null,
  usageDetail: null,
  product: null,
  templates: null,
  usageSummaryData: [],
  usageSummaryTable: [],
}

export const InvoiceViewSlice = createSlice({
  name: 'InvoiceViewSlice',
  initialState,
  reducers: {
    setData(state, action: PayloadAction<InvoiceViewState>) {
      state.invoiceInformation = action.payload.invoiceInformation
      state.usageDetail = action.payload.usageDetail
      state.product = action.payload.product
      state.templates = action.payload.templates
    },
    setInvoiceInformation(
      state,
      action: PayloadAction<InvoiceInformation | null>,
    ) {
      state.invoiceInformation = action.payload
    },
    setUsageSummaryData(state, action: PayloadAction<DataTableUsageSummary[]>) {
      state.usageSummaryData = JSON.parse(JSON.stringify(action.payload))
    },
    setTemplates(state, action: PayloadAction<ProductTemplateType[]>) {
      state.templates = action.payload
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload
    },
    resetEmptyData(state) {
      state.invoiceInformation = null
      state.usageSummaryData = []
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getInvoiceData.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(
      getInvoiceData.fulfilled,
      (state, action: PayloadAction<InvoiceViewState>) => {
        state.loading = false
        const {
          invoiceInformation,
          product,
          templates,
          usageDetail,
          usageSummaryData,
          usageSummaryTable,
        } = action.payload
        state.invoiceInformation = invoiceInformation
        state.product = product
        state.templates = templates
        state.usageDetail = usageDetail
        state.usageSummaryData = usageSummaryData
        state.usageSummaryTable = usageSummaryTable
        return state
      },
    )
    builder.addCase(getInvoiceData.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload
    })

    builder.addCase(createInvoice.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(createInvoice.fulfilled, (state, action) => {
      state.loading = false
      state.invoiceInformation = action.payload
    })
    builder.addCase(createInvoice.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload
    })

    builder.addCase(updateInvoice.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(updateInvoice.fulfilled, (state, action) => {
      state.loading = false
      state.invoiceInformation = action.payload
    })
    builder.addCase(updateInvoice.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload
    })
  },
})

export const {
  setData,
  setInvoiceInformation,
  setUsageSummaryData,
  setTemplates,
  setError,
  resetEmptyData,
} = InvoiceViewSlice.actions

export const selectLoading = (state: RootState) => state.invoiceView.loading
export const selectError = (state: RootState) => state.invoiceView.error
export const selectInvoiceInformation = (state: RootState) =>
  state.invoiceView.invoiceInformation
export const selectConsumer = (state: RootState) =>
  state.invoiceView.invoiceInformation?.consumer
export const selectBillingSummary = (state: RootState) =>
  state.invoiceView.invoiceInformation?.billing
export const selectProvider = (state: RootState) =>
  state.invoiceView.invoiceInformation?.supplier
export const selectUsageDetail = (state: RootState) =>
  state.invoiceView.usageDetail
export const selectProduct = (state: RootState) => state.invoiceView.product
export const selectTemplates = (state: RootState) => state.invoiceView.templates
export const selectUsageSummaryData = (state: RootState) =>
  state.invoiceView.usageSummaryData
export const selectBaseFees = (state: RootState) =>
  state.invoiceView.product?.pkgInfo?.baseFees || []
export const selectUsageSummaryTable = (state: RootState) =>
  state.invoiceView.usageSummaryTable

export default InvoiceViewSlice.reducer
